/**
 * GM info bar: balanced 2-line text + horizontal scroll-snap carousel.
 */
(function () {
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {HTMLSpanElement | null} */
  let measureEl = null;

  /** Valor monetário BR: "R$" + valor = um único token. */
  const MONEY_TOKEN_RE = /^R\$\s*\d{1,3}(?:\.\d{3})*(?:,\d{2})?$/i;
  const TOKEN_RE = /R\$\s*\d{1,3}(?:\.\d{3})*(?:,\d{2})?|\S+/gu;

  /** Primeira linha deve ser visivelmente maior que a segunda, nunca igual. */
  const MIN_FIRST_LINE_GAP_PX = 8;

  /**
   * @param {string} text
   * @returns {string[]}
   */
  function tokenize(text) {
    const tokens = [];
    let match;

    TOKEN_RE.lastIndex = 0;
    while ((match = TOKEN_RE.exec(text)) !== null) {
      tokens.push(match[0]);
    }

    return tokens;
  }

  /**
   * @param {string} token
   * @returns {string}
   */
  function formatToken(token) {
    if (MONEY_TOKEN_RE.test(token)) {
      return token.replace(/\s+/, '\u00A0');
    }
    return token;
  }

  /**
   * @param {string[]} tokens
   * @param {number} start
   * @param {number} end
   * @returns {string}
   */
  function tokensToLineText(tokens, start, end) {
    return tokens.slice(start, end).map(formatToken).join(' ');
  }

  /**
   * @param {HTMLElement} reference
   * @returns {HTMLSpanElement}
   */
  function getMeasurer(reference) {
    if (!measureEl) {
      measureEl = document.createElement('span');
      measureEl.setAttribute('aria-hidden', 'true');
      measureEl.style.cssText =
        'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:nowrap;pointer-events:none;';
      document.body.appendChild(measureEl);
    }

    const cs = getComputedStyle(reference);
    measureEl.style.fontFamily = cs.fontFamily;
    measureEl.style.fontSize = cs.fontSize;
    measureEl.style.fontWeight = cs.fontWeight;
    measureEl.style.fontStyle = cs.fontStyle;
    measureEl.style.letterSpacing = cs.letterSpacing;

    return measureEl;
  }

  /**
   * @param {string} text
   * @param {HTMLElement} reference
   * @returns {number}
   */
  function measureLineWidth(text, reference) {
    const el = getMeasurer(reference);
    el.textContent = text;
    return el.getBoundingClientRect().width;
  }

  /**
   * @param {string[]} tokens
   * @param {HTMLElement} reference
   * @param {number} maxWidth
   * @returns {number} índice onde a linha 2 começa
   */
  function findBalancedSplit(tokens, reference, maxWidth) {
    if (tokens.length < 2) return tokens.length;

    let bestSplit = tokens.length - 1;

    for (let split = tokens.length - 1; split >= 1; split--) {
      const line1 = tokensToLineText(tokens, 0, split);
      const line2 = tokensToLineText(tokens, split, tokens.length);
      const width1 = measureLineWidth(line1, reference);
      const width2 = measureLineWidth(line2, reference);

      if (width1 > maxWidth) {
        bestSplit = split;
        continue;
      }

      if (width2 > maxWidth) {
        break;
      }

      if (width1 > width2 + MIN_FIRST_LINE_GAP_PX) {
        bestSplit = split;
      } else {
        break;
      }
    }

    return bestSplit;
  }

  /**
   * @param {HTMLElement} el
   * @param {string} text
   */
  function renderLine(el, text) {
    el.textContent = text;
  }

  /**
   * @param {HTMLElement} el
   */
  function balanceTextElement(el) {
    const source = el.dataset.gmBalanceSource || el.textContent?.trim() || '';
    if (!source) return;

    el.dataset.gmBalanceSource = source;

    const maxWidth = el.clientWidth;
    if (maxWidth < 1) return;

    const tokens = tokenize(source);
    const fullLine = tokensToLineText(tokens, 0, tokens.length);

    if (tokens.length < 2 || measureLineWidth(fullLine, el) <= maxWidth) {
      el.replaceChildren();
      const line = document.createElement('span');
      line.className = 'gm-info-bar__line';
      renderLine(line, fullLine);
      el.append(line);
      return;
    }

    const split = findBalancedSplit(tokens, el, maxWidth);
    const line1Text = tokensToLineText(tokens, 0, split);
    const line2Text = tokensToLineText(tokens, split, tokens.length);

    const line1 = document.createElement('span');
    line1.className = 'gm-info-bar__line';
    renderLine(line1, line1Text);

    const line2 = document.createElement('span');
    line2.className = 'gm-info-bar__line';
    renderLine(line2, line2Text);

    el.replaceChildren(line1, line2);
  }

  /**
   * @param {HTMLElement} root
   */
  function balanceInfoBar(root) {
    root.querySelectorAll('.gm-info-bar__text').forEach(balanceTextElement);
  }

  /**
   * @param {HTMLElement} root
   */
  function initCarousel(root) {
    if (root.dataset.gmCarouselInit === '1') return;

    const track = root.querySelector('.gm-info-bar__track');
    if (!track) return;

    const slides = [...track.querySelectorAll('.gm-info-bar__slide')];
    if (slides.length < 2) return;

    root.dataset.gmCarouselInit = '1';

    const autoRotate = root.getAttribute('data-auto-rotate') === 'true';
    const intervalMs = Math.max(2500, parseInt(root.getAttribute('data-interval-ms') || '5000', 10));

    let index = 0;
    /** @type {ReturnType<typeof setInterval> | null} */
    let timer = null;

    const slideWidth = () => track.clientWidth;

    function goTo(i) {
      const n = slides.length;
      index = ((i % n) + n) % n;
      const w = slideWidth();
      track.scrollTo({ left: index * w, behavior: 'smooth' });
    }

    function syncFromScroll() {
      const w = slideWidth();
      if (w < 1) return;
      index = Math.round(track.scrollLeft / w);
    }

    function armTimer() {
      clearInterval(timer);
      timer = null;
      if (!autoRotate || reducedMotion.matches) return;
      timer = setInterval(() => goTo(index + 1), intervalMs);
    }

    function pause() {
      clearInterval(timer);
      timer = null;
    }

    track.addEventListener(
      'scroll',
      () => {
        syncFromScroll();
      },
      { passive: true }
    );

    root.addEventListener('pointerenter', pause);
    root.addEventListener('pointerleave', armTimer);
    root.addEventListener('focusin', pause);
    root.addEventListener('focusout', armTimer);

    reducedMotion.addEventListener('change', () => {
      pause();
      armTimer();
    });

    window.addEventListener(
      'resize',
      () => {
        const w = slideWidth();
        track.scrollLeft = index * w;
      },
      { passive: true }
    );

    armTimer();
  }

  /**
   * @param {HTMLElement} root
   */
  function init(root) {
    balanceInfoBar(root);
    initCarousel(root);
  }

  /** @param {(...args: unknown[]) => void} fn @param {number} ms */
  function debounce(fn, ms) {
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let timeoutId;
    return (...args) => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn(...args), ms);
    };
  }

  const rebalanceAll = debounce(() => {
    document.querySelectorAll('[data-gm-info-bar]').forEach(balanceInfoBar);
  }, 100);

  function boot() {
    document.querySelectorAll('[data-gm-info-bar]').forEach(init);
    requestAnimationFrame(rebalanceAll);
  }

  function scheduleBoot() {
    const start = () => {
      if (document.fonts?.ready) {
        document.fonts.ready.then(boot).catch(boot);
      } else {
        boot();
      }
    };
    if ('requestIdleCallback' in window) {
      requestIdleCallback(start, { timeout: 1800 });
    } else {
      window.addEventListener('load', start, { once: true });
    }
  }

  scheduleBoot();

  window.addEventListener('resize', rebalanceAll, { passive: true });

  if (typeof ResizeObserver !== 'undefined') {
    const observer = new ResizeObserver(rebalanceAll);
    document.querySelectorAll('[data-gm-info-bar]').forEach((root) => observer.observe(root));
  }
})();
